import { ThumbsDown, ThumbsUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import {
    RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER,
    type RunFeedbackLabel,
    RunFeedbackPrompt,
} from '@apps/web/components/shared/run_feedback/run_feedback_prompt';
import { Button } from '@apps/web/components/ui/button';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@apps/web/components/ui/popover';
import { useTrainingRunActions } from '@apps/web/features/teach_phoebe/model/use_training_run_actions';
import { ChatRunTranscriptDialog } from '@apps/web/features/teach_phoebe/ui/chat_run_transcript_dialog';
import { toast } from '@apps/web/lib/app_notifications';
import { formatRelativeTime } from '@apps/web/lib/date_utils';
import { runTitle } from '@apps/web/lib/training_run_utils';
import { cn } from '@apps/web/lib/utils';
import { type TrainingRunRead } from '@libraries/typescript/api_client';

export function RunRow({
    run,
    isLast,
    organizationId,
    onFeedbackSaved,
    onReviewingChange,
}: {
    readonly run: TrainingRunRead;
    readonly isLast: boolean;
    readonly organizationId: string | null;
    readonly onFeedbackSaved: (
        runId: string,
        label: 'good' | 'bad' | null,
    ) => void;
    readonly onReviewingChange: (runId: string, isReviewing: boolean) => void;
}) {
    const { openRun, saveReview, clearReview } =
        useTrainingRunActions(organizationId);
    const [isSaving, setIsSaving] = useState(false);
    // Local override of the row's label so a click reflects immediately: the
    // run prop only catches up once the overview revalidates. `undefined` means
    // "defer to the prop"; a set value (including null for an unclick) wins
    // until the next action, so both thumbs always show the current choice.
    const [override, setOverride] = useState<
        RunFeedbackLabel | null | undefined
    >(undefined);
    const selectedLabel: RunFeedbackLabel | null =
        override !== undefined ? override : (run.feedback_label ?? null);
    // The thumbs-down note overlay. A down-vote must carry the coordinator's
    // words — what went wrong is the teaching signal — so nothing persists
    // until Save.
    const [isNoteOpen, setIsNoteOpen] = useState(false);
    const [noteDraft, setNoteDraft] = useState('');
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
    const summaryText = run.summary ?? run.description;
    // The note anchors on the thumbs group (not a trigger), so Radix can't
    // restore focus itself on close — do it manually back to the thumbs-down.
    const thumbsDownRef = useRef<HTMLButtonElement>(null);

    // Release a still-open review pin if the row unmounts mid-note (a filter
    // switch or refresh removes it), so it isn't stuck pinned with no UI.
    const isNoteOpenRef = useRef(isNoteOpen);
    isNoteOpenRef.current = isNoteOpen;
    useEffect(() => {
        return () => {
            if (isNoteOpenRef.current) {
                onReviewingChange(run.id, false);
            }
        };
    }, [onReviewingChange, run.id]);

    const persist = async (
        action: () => Promise<void>,
        nextLabel: RunFeedbackLabel | null,
    ): Promise<boolean> => {
        setIsSaving(true);
        try {
            await action();
            setOverride(nextLabel);
            onFeedbackSaved(run.id, nextLabel);
            return true;
        } catch {
            toast.error('Failed to save the review. Please try again.', {
                persistNotification: false,
            });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const rate = (label: RunFeedbackLabel, comment?: string) =>
        persist(() => saveReview({ run, label, comment }), label);
    const clear = () => persist(() => clearReview(run), null);

    const handleThumb = (label: RunFeedbackLabel) => {
        if (!organizationId || isSaving) {
            return;
        }
        // Clicking the selected thumb again unclicks it (people change their
        // mind); clicking the other switches to it.
        if (selectedLabel === label) {
            void clear();
            return;
        }
        if (label === 'good') {
            // Switching to a positive rating dismisses any open thumbs-down
            // note so the row doesn't stay pinned with a stale note editor.
            if (isNoteOpen) {
                closeNote();
            }
            void rate('good');
            return;
        }
        // A thumbs-down needs a note before it saves. Pin the row open in the
        // Unreviewed feed while the note overlay is up so a sibling save can't
        // drop it mid-review.
        setNoteDraft('');
        onReviewingChange(run.id, true);
        setIsNoteOpen(true);
    };

    const closeNote = () => {
        setIsNoteOpen(false);
        setNoteDraft('');
        onReviewingChange(run.id, false);
    };

    const handleSaveNote = async () => {
        const note = noteDraft.trim();
        if (!organizationId || !note) {
            return;
        }
        // Keep the note editor open if the save failed, so the coordinator's
        // words aren't discarded on a transient error.
        if (await rate('bad', note)) {
            closeNote();
        }
    };

    const timeLabel = formatRelativeTime(run.last_activity_at);
    const titleBlock = (
        <React.Fragment>
            <p className="truncate text-sm font-medium">{runTitle(run)}</p>
            <p className="text-muted-foreground truncate text-xs">
                {timeLabel}
            </p>
        </React.Fragment>
    );

    return (
        <li
            className={cn(
                'hover:bg-muted/40 relative py-3 transition-colors',
                !isLast && 'border-b',
            )}
        >
            <div className="flex items-center gap-3">
                {/* The title button stretches an invisible overlay across the
                    whole row (summary and padding included) so anywhere in the
                    row opens the run, while staying one focusable control with
                    a real accessible name. The thumbs sit above the overlay. */}
                <button
                    type="button"
                    onClick={() => {
                        if (openRun(run) === 'chat-transcript') {
                            setIsTranscriptOpen(true);
                        }
                    }}
                    className="min-w-0 flex-1 cursor-pointer text-left after:absolute after:inset-0 after:content-['']"
                    title={runTitle(run)}
                >
                    {titleBlock}
                </button>
                <Popover
                    open={isNoteOpen}
                    onOpenChange={(open) => {
                        if (!open) {
                            closeNote();
                        }
                    }}
                >
                    <PopoverAnchor asChild={true}>
                        <div className="relative z-10 flex shrink-0 items-center gap-1">
                            <Button
                                variant="outline"
                                size="icon"
                                className={cn(
                                    selectedLabel === 'good' &&
                                        'border-green-200 bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800',
                                )}
                                disabled={isSaving}
                                onClick={() => handleThumb('good')}
                                title="Looks good"
                                aria-label="Looks good"
                                aria-pressed={selectedLabel === 'good'}
                            >
                                <ThumbsUp
                                    className={cn(
                                        'h-4 w-4',
                                        selectedLabel === 'good' &&
                                            'fill-current',
                                    )}
                                />
                            </Button>
                            <Button
                                ref={thumbsDownRef}
                                variant="outline"
                                size="icon"
                                className={cn(
                                    selectedLabel === 'bad' &&
                                        'border-red-200 bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-800',
                                )}
                                disabled={isSaving}
                                onClick={() => handleThumb('bad')}
                                title="Needs work"
                                aria-label="Needs work"
                                aria-pressed={selectedLabel === 'bad'}
                            >
                                <ThumbsDown
                                    className={cn(
                                        'h-4 w-4',
                                        selectedLabel === 'bad' &&
                                            'fill-current',
                                    )}
                                />
                            </Button>
                        </div>
                    </PopoverAnchor>
                    <PopoverContent
                        align="end"
                        className="w-[min(calc(100vw-2rem),20rem)] border-0 bg-transparent p-0 shadow-none"
                        onCloseAutoFocus={(event) => {
                            event.preventDefault();
                            thumbsDownRef.current?.focus();
                        }}
                    >
                        <RunFeedbackPrompt
                            state="note"
                            comment={noteDraft}
                            disabled={isSaving}
                            requireComment={true}
                            autoFocusComment={true}
                            submitOnEnter={true}
                            commentPlaceholder={
                                RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER
                            }
                            onCommentChange={setNoteDraft}
                            onSaveComment={() => void handleSaveNote()}
                            onSkipComment={closeNote}
                            onShowNote={() => undefined}
                        />
                    </PopoverContent>
                </Popover>
            </div>
            {summaryText ? (
                <p className="text-muted-foreground mt-2 text-xs leading-5">
                    {summaryText}
                </p>
            ) : null}
            {/* Mounted only while open so the run event stream stays
                disconnected for every closed row. */}
            {isTranscriptOpen && organizationId ? (
                <ChatRunTranscriptDialog
                    run={run}
                    organizationId={organizationId}
                    onClose={() => setIsTranscriptOpen(false)}
                />
            ) : null}
        </li>
    );
}
